// 2026-06-20 (user request) — Finance Metric Info Dialog.
// Tombol (i) di tiap kartu KPI ("chevron") Laporan Keuangan → tab Ringkasan.
// Pola identik dgn WOStageInfoDialog (Monitoring WO): deskripsi default bawaan
// sistem + custom note yang bisa diedit BOD Lv1 (persist ke /api/finance-metric-notes/{key}).
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pencil, X } from "lucide-react";

export const FIN_METRIC_META = {
  total_invoice: { key: "total_invoice", label: "Total Invoice" },
  kas_harus_diterima: { key: "kas_harus_diterima", label: "Kas Harus Diterima" },
  sudah_diterima: { key: "sudah_diterima", label: "Sudah Diterima" },
  sisa_piutang: { key: "sisa_piutang", label: "Sisa Piutang" },
  pengeluaran: { key: "pengeluaran", label: "Pengeluaran" },
  retensi_ditahan: { key: "retensi_ditahan", label: "Retensi Ditahan" },
  // 2026-06-20 (user request): tombol (i) juga di grafik donut.
  donut_penerimaan_kas: { key: "donut_penerimaan_kas", label: "Grafik: Progress Pembayaran (Penerimaan Kas)" },
  donut_profit: { key: "donut_profit", label: "Grafik: Profit" },
};

// Deskripsi bawaan sistem — menjelaskan RUMUS angka di tiap kartu.
// Bisa di-override BOD Lv1 lewat tombol Edit (tersimpan di DB).
const METRIC_DEFAULT_DESCRIPTIONS = {
  total_invoice: {
    body: `DEFINISI:
Total nilai seluruh invoice reguler (non-retensi) yang tercatat pada proyek-proyek yang sedang terfilter. Invoice klaim retensi (5%) TIDAK dihitung di kartu ini — dicatat terpisah sebagai retensi diklaim.

RUMUS:
Σ amount dari semua invoice dengan is_retention_claim = false.

CATATAN:
• Angka mengikuti filter aktif (tahun/bulan/proyek/client/status) dan toggle "Sembunyikan data TEST_*".
• Baris induk di tabel = gabungan Main Project + seluruh VO/WO di bawahnya.`,
  },
  kas_harus_diterima: {
    body: `DEFINISI:
Ekspektasi kas NET yang seharusnya diterima dari seluruh invoice reguler — nilai invoice setelah dikurangi potongan PPh dan retensi (tipe inside).

RUMUS:
Per invoice: max(0, amount − pph_amount − retention_amount). Bila billing engine menyimpan snapshot net_received_amount, nilai itu yang dipakai (paling akurat). Total kartu = Σ expected net semua invoice reguler.

CATATAN:
• PPh yang dipotong client BUKAN piutang — pajak sudah disetor ke DJP atas nama ENS, sehingga tidak pernah diharapkan masuk kas.
• Retensi tipe "outside" tidak mengurangi angka ini (ditagih terpisah di akhir masa retensi).`,
  },
  sudah_diterima: {
    body: `DEFINISI:
Total kas yang BENAR-BENAR sudah diterima dari seluruh invoice, berdasarkan riwayat cicilan pembayaran yang tercatat.

RUMUS:
Σ payment_receipts.amount per invoice. Invoice legacy tanpa rincian cicilan tapi dengan tanggal diterima (date) terisi → dianggap lunas sebesar Kas Harus Diterima invoice tersebut.

CATATAN:
• Invoice dengan tanggal diterima kosong = menunggu pembayaran → dihitung Rp 0 di kartu ini.`,
  },
  sisa_piutang: {
    body: `DEFINISI:
Selisih yang masih harus ditagih kepada client atas invoice yang sudah terbit.

RUMUS:
Per invoice: max(0, Kas Harus Diterima − Sudah Diterima). Total kartu = Σ sisa semua invoice.

CATATAN:
• Sisa Rp 0 berarti invoice LUNAS (status pill hijau di tabel).
• Sisa besar dengan umur panjang masuk radar Collection / Janji Bayar.`,
  },
  pengeluaran: {
    body: `DEFINISI:
Total seluruh pengeluaran/biaya yang dicatat pada proyek-proyek terfilter.

RUMUS:
Σ amount dari expense_entries (Pengeluaran Project per proyek).

CATATAN:
• Kas keluar riil proyek = angka ini; bandingkan dengan "Sudah Diterima" untuk membaca posisi kas proyek.`,
  },
  retensi_ditahan: {
    body: `DEFINISI:
Total retensi yang dipotong client dari invoice reguler (umumnya 5% dari nilai pekerjaan) — baru bisa ditagih setelah masa retensi berakhir.

RUMUS:
Σ retention_amount dari semua invoice reguler (non-klaim).

CATATAN:
• Pencairan retensi dicatat lewat invoice klaim retensi (is_retention_claim = true) → masuk kartu "Sudah Diterima" saat dibayar.
• Sisa retensi yang belum diklaim per proyek dihitung sebagai Retensi Ditahan − Sudah Diklaim.`,
  },
  donut_penerimaan_kas: {
    body: `DEFINISI:
Seberapa besar kas yang sudah diterima dibanding total kas yang seharusnya diterima dari seluruh invoice yang sedang terfilter.

RUMUS:
Persen (tengah grafik) = Sudah Diterima ÷ Kas Harus Diterima × 100%.
Sudah Diterima = Σ payment_receipts.amount (invoice legacy tanpa rincian cicilan tapi bertanggal diterima dihitung lunas).
Kas Harus Diterima = Σ (amount − PPh − retensi inside) per invoice reguler.
Sisa Piutang = Kas Harus Diterima − Sudah Diterima.

CATATAN:
• Mengikuti semua filter aktif (tahun/bulan/proyek/client/status/cari).`,
  },
  donut_profit: {
    body: `DEFINISI:
Margin — selisih total invoice dengan pengeluaran yang tercatat, atas data yang sedang terfilter.

RUMUS:
Profit = Total Invoice − Pengeluaran.
Margin % (tengah grafik) = Profit ÷ Total Invoice × 100%.
Segmen hijau = profit, segmen merah = pengeluaran, terhadap dasar Total Invoice.

CATATAN:
• Bila pengeluaran melebihi total invoice, nilai profit negatif ditandai merah.
• Mengikuti semua filter aktif (tahun/bulan/proyek/client/status/cari).`,
  },
};

export default function FinanceMetricInfoDialog({ open, onOpenChange, metricKey, user }) {
  const metric = FIN_METRIC_META[metricKey] || null;
  const [note, setNote] = useState(null); // { content, updated_at, updated_by_name } | null
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = user?.division === "BOD" && Number(user?.level || 5) === 1;

  useEffect(() => {
    if (!open || !metricKey) return;
    setEditing(false);
    setLoading(true);
    api.get("/finance-metric-notes")
      .then(({ data }) => {
        const n = data?.[metricKey];
        setNote(n || null);
        setDraft(n?.content || "");
      })
      .catch(() => setNote(null))
      .finally(() => setLoading(false));
  }, [open, metricKey]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/finance-metric-notes/${metricKey}`, { content: draft });
      setNote(data);
      setEditing(false);
      toast.success("Keterangan tersimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  if (!metric) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto rounded-none" data-testid="fin-metric-info-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center border border-slate-400 text-slate-700 font-bold">i</span>
            {metric.label}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Rumus / cara perhitungan angka kartu ini. {isSuperAdmin ? "Anda (BOD Lv1) bisa mengubah isinya." : "Hanya Super Admin (BOD Lv1) yang bisa mengubah isinya."}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-[120px] py-2" data-testid="fin-metric-info-body">
          {loading ? (
            <div className="text-xs text-slate-400 italic">Memuat...</div>
          ) : editing ? (
            <textarea
              data-testid="fin-metric-info-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={8}
              maxLength={2000}
              placeholder="Tulis keterangan rumus metrik ini..."
              className="w-full rounded-none border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A1F]"
            />
          ) : (note?.content ? (
            <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="fin-metric-info-custom">{note.content}</div>
          ) : (
            (() => {
              const def = METRIC_DEFAULT_DESCRIPTIONS[metricKey];
              if (!def) return <div className="text-xs text-slate-400 italic">Belum ada keterangan untuk metrik ini.</div>;
              const lines = String(def.body || "").split("\n");
              return (
                <div data-testid="fin-metric-info-default" className="space-y-0.5 text-sm text-slate-800 leading-relaxed">
                  {lines.map((ln, i) => {
                    const trimmed = ln.trim();
                    if (/^DEFINISI:?$/i.test(trimmed))
                      return <div key={i} className="text-[10px] font-bold uppercase tracking-wider text-[#0B1D3A] pt-2 first:pt-0">Definisi</div>;
                    if (/^RUMUS:?$/i.test(trimmed))
                      return <div key={i} className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 pt-3">Rumus Perhitungan</div>;
                    if (/^CATATAN:?$/i.test(trimmed))
                      return <div key={i} className="text-[10px] font-bold uppercase tracking-wider text-amber-700 pt-3">Catatan</div>;
                    if (trimmed === "") return <div key={i} className="h-1" />;
                    return <div key={i} className="whitespace-pre-wrap">{ln}</div>;
                  })}
                  <div className="text-[10px] text-slate-400 italic mt-4 border-t border-slate-100 pt-2">
                    Ini keterangan bawaan sistem. {isSuperAdmin ? "Klik Edit untuk override dengan versi custom." : "Hanya Super Admin (BOD Lv1) yang bisa mengubah."}
                  </div>
                </div>
              );
            })()
          ))}
          {note?.updated_at && !editing && (
            <div className="text-[10px] text-slate-400 italic mt-3 border-t border-slate-100 pt-2">
              Diubah oleh {note.updated_by_name || "-"} · {new Date(note.updated_at).toLocaleString("id-ID")}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {isSuperAdmin && !editing && (
            <Button onClick={() => { setDraft(note?.content || ""); setEditing(true); }} variant="outline" className="rounded-none" data-testid="fin-metric-info-edit-btn">
              <Pencil size={12} className="mr-1" /> Edit
            </Button>
          )}
          {editing && (
            <>
              <Button variant="ghost" onClick={() => setEditing(false)} className="rounded-none" data-testid="fin-metric-info-cancel-btn">
                <X size={12} className="mr-1" /> Batal
              </Button>
              <Button onClick={save} disabled={saving} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white" data-testid="fin-metric-info-save-btn">
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </>
          )}
          {!editing && (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Tutup</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
